import { useCallback, useEffect, useState } from "react";
import { Check, FolderKanban, Loader2, LogOut, Pencil, Plus, Star, Trash2, Users, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const STATUS_OPTIONS = [
  { value: "novo", label: "Novo" },
  { value: "em_contato", label: "Em contato" },
  { value: "convertido", label: "Convertido" },
  { value: "arquivado", label: "Arquivado" },
];

const EMPTY_PROJECT = {
  client_name: "",
  segment: "",
  service_type: "",
  challenge: "",
  solution: "",
  results: "",
  image_url: "",
  published: true,
};

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-gb-white placeholder:text-gb-gray/50 focus:border-gb-silver/50 focus:outline-none";

const StarsRow = ({ value }) => (
  <span className="flex gap-0.5" aria-label={`Nota ${value} de 5`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`h-3.5 w-3.5 ${i < value ? "fill-gb-silver text-gb-silver" : "text-gb-silver/30"}`} aria-hidden="true" />
    ))}
  </span>
);

const LeadsTab = () => {
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get("/leads").then((r) => setLeads(r.data)).catch((e) => setError(formatApiError(e)));
  }, []);

  useEffect(load, [load]);

  const setStatus = async (lead, status) => {
    await api.patch(`/leads/${lead.id}`, { status });
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
  };

  const remove = async (lead) => {
    if (!window.confirm(`Remover o lead de ${lead.name}?`)) return;
    await api.delete(`/leads/${lead.id}`);
    setLeads((ls) => ls.filter((l) => l.id !== lead.id));
  };

  if (error) return <p className="text-sm text-red-400" data-testid="leads-error">{error}</p>;
  if (!leads) return <Loader2 className="h-5 w-5 animate-spin text-gb-silver" aria-label="Carregando leads" />;
  if (leads.length === 0)
    return (
      <p className="rounded-xl border border-dashed border-white/15 px-6 py-12 text-center text-sm text-gb-gray" data-testid="leads-empty">
        Nenhum lead recebido ainda. Os envios do formulário do site aparecem aqui.
      </p>
    );

  return (
    <ul className="space-y-4" data-testid="leads-list">
      {leads.map((lead) => (
        <li key={lead.id} data-testid={`lead-item-${lead.id}`} className="card-chrome rounded-xl p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-display text-base font-bold">{lead.name}</p>
              <p className="text-sm text-gb-silver">{lead.company}</p>
              <p className="mt-1 text-sm text-gb-gray">{lead.contact}</p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={lead.status}
                onChange={(e) => setStatus(lead, e.target.value)}
                data-testid={`lead-status-${lead.id}`}
                aria-label={`Status do lead ${lead.name}`}
                className="rounded-lg border border-white/10 bg-gb-ink px-3 py-2 text-xs text-gb-white"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <button type="button" onClick={() => remove(lead)} data-testid={`lead-delete-${lead.id}`} aria-label={`Remover lead ${lead.name}`} className="rounded-lg border border-white/10 p-2 text-gb-gray transition-colors hover:border-red-500/40 hover:text-red-400">
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-gb-gray">
            <span className="rounded-full border border-white/10 px-3 py-1">{lead.segment}</span>
            <span className="rounded-full border border-white/10 px-3 py-1">{lead.service}</span>
            <span className="rounded-full border border-white/10 px-3 py-1">{lead.goal}</span>
          </div>
          {lead.message && <p className="mt-3 text-sm leading-relaxed text-gb-gray">“{lead.message}”</p>}
          <p className="mt-3 font-mono text-[10px] text-gb-gray/60">
            {new Date(lead.created_at).toLocaleString("pt-BR")}
            {lead.utm_source ? ` · origem: ${lead.utm_source}${lead.utm_medium ? `/${lead.utm_medium}` : ""}${lead.utm_campaign ? `/${lead.utm_campaign}` : ""}` : ""}
          </p>
        </li>
      ))}
    </ul>
  );
};

const ProjectsTab = () => {
  const [projects, setProjects] = useState(null);
  const [form, setForm] = useState(EMPTY_PROJECT);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get("/projects/all").then((r) => setProjects(r.data)).catch((e) => setError(formatApiError(e)));
  }, []);

  useEffect(load, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startEdit = (p) => {
    const { id, created_at, ...rest } = p;
    setForm({ ...EMPTY_PROJECT, ...rest });
    setEditingId(id);
    setShowForm(true);
  };

  const reset = () => {
    setForm(EMPTY_PROJECT);
    setEditingId(null);
    setShowForm(false);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editingId) await api.put(`/projects/${editingId}`, form);
      else await api.post("/projects", form);
      reset();
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remover o projeto ${p.client_name}?`)) return;
    await api.delete(`/projects/${p.id}`);
    setProjects((ps) => ps.filter((x) => x.id !== p.id));
  };

  if (!projects) return <Loader2 className="h-5 w-5 animate-spin text-gb-silver" aria-label="Carregando projetos" />;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-gb-gray">{projects.length} projeto(s) cadastrado(s)</p>
        <button type="button" onClick={() => { reset(); setShowForm(true); }} data-testid="project-add-btn" className="btn-chrome inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold">
          <Plus className="h-4 w-4" aria-hidden="true" />
          Novo projeto
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} data-testid="project-form" className="card-chrome mb-8 space-y-4 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-base font-bold">{editingId ? "Editar projeto" : "Novo projeto"}</h3>
            <button type="button" onClick={reset} aria-label="Fechar formulário" className="text-gb-gray hover:text-gb-white">
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <input required placeholder="Nome do cliente/projeto" aria-label="Nome do cliente" data-testid="project-client-input" className={inputCls} value={form.client_name} onChange={(e) => set("client_name", e.target.value)} />
            <input required placeholder="Segmento (ex.: Barbearia)" aria-label="Segmento" data-testid="project-segment-input" className={inputCls} value={form.segment} onChange={(e) => set("segment", e.target.value)} />
            <input required placeholder="Serviço (ex.: Site + Tráfego)" aria-label="Serviço realizado" data-testid="project-service-input" className={inputCls} value={form.service_type} onChange={(e) => set("service_type", e.target.value)} />
          </div>
          <input placeholder="URL da imagem (opcional)" aria-label="URL da imagem" data-testid="project-image-input" className={inputCls} value={form.image_url} onChange={(e) => set("image_url", e.target.value)} />
          <div className="grid gap-4 sm:grid-cols-2">
            <textarea placeholder="Desafio" aria-label="Desafio" rows={2} className={`${inputCls} resize-none`} value={form.challenge} onChange={(e) => set("challenge", e.target.value)} />
            <textarea placeholder="Solução implementada" aria-label="Solução implementada" rows={2} className={`${inputCls} resize-none`} value={form.solution} onChange={(e) => set("solution", e.target.value)} />
          </div>
          <input placeholder="Resultados documentados (opcional)" aria-label="Resultados" data-testid="project-results-input" className={inputCls} value={form.results} onChange={(e) => set("results", e.target.value)} />
          <label className="flex items-center gap-2 text-xs text-gb-gray">
            <input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} data-testid="project-published-checkbox" className="h-4 w-4 appearance-none rounded border border-white/20 bg-white/[0.03] checked:border-gb-silver checked:bg-gb-silver" />
            Publicado no site
          </label>
          {error && <p className="text-xs text-red-400" role="alert">{error}</p>}
          <button type="submit" disabled={saving} data-testid="project-save-btn" className="btn-chrome inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-xs font-semibold disabled:opacity-60">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
            {editingId ? "Salvar alterações" : "Adicionar projeto"}
          </button>
        </form>
      )}

      {projects.length === 0 ? (
        <p className="rounded-xl border border-dashed border-white/15 px-6 py-12 text-center text-sm text-gb-gray" data-testid="projects-empty">
          Nenhum projeto cadastrado ainda.
        </p>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2" data-testid="projects-list">
          {projects.map((p) => (
            <li key={p.id} data-testid={`project-item-${p.id}`} className="card-chrome rounded-xl p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display text-base font-bold">{p.client_name}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-gb-gray">
                    {p.segment} · {p.service_type} · {p.published ? "publicado" : "rascunho"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => startEdit(p)} data-testid={`project-edit-${p.id}`} aria-label={`Editar ${p.client_name}`} className="rounded-lg border border-white/10 p-2 text-gb-gray transition-colors hover:border-gb-silver/40 hover:text-gb-white">
                    <Pencil className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button type="button" onClick={() => remove(p)} data-testid={`project-delete-${p.id}`} aria-label={`Remover ${p.client_name}`} className="rounded-lg border border-white/10 p-2 text-gb-gray transition-colors hover:border-red-500/40 hover:text-red-400">
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
              {p.solution && <p className="mt-3 text-sm leading-relaxed text-gb-gray">{p.solution}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ReviewsTab = () => {
  const [reviews, setReviews] = useState(null);

  const load = useCallback(() => {
    api.get("/reviews/all").then((r) => setReviews(r.data)).catch(() => setReviews([]));
  }, []);

  useEffect(load, [load]);

  const setApproved = async (rev, approved) => {
    await api.patch(`/reviews/${rev.id}`, { approved });
    setReviews((rs) => rs.map((r) => (r.id === rev.id ? { ...r, approved } : r)));
  };

  const remove = async (rev) => {
    if (!window.confirm(`Remover a avaliação de ${rev.name}?`)) return;
    await api.delete(`/reviews/${rev.id}`);
    setReviews((rs) => rs.filter((r) => r.id !== rev.id));
  };

  if (!reviews) return <Loader2 className="h-5 w-5 animate-spin text-gb-silver" aria-label="Carregando avaliações" />;
  if (reviews.length === 0)
    return (
      <p className="rounded-xl border border-dashed border-white/15 px-6 py-12 text-center text-sm text-gb-gray" data-testid="reviews-empty">
        Nenhuma avaliação recebida ainda. Quando um cliente avaliar pelo site, aparece aqui para você aprovar.
      </p>
    );

  return (
    <ul className="space-y-4" data-testid="reviews-list">
      {reviews.map((rev) => (
        <li key={rev.id} data-testid={`review-item-${rev.id}`} className="card-chrome rounded-xl p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <p className="font-display text-base font-bold">{rev.name}</p>
                <StarsRow value={rev.rating} />
              </div>
              {rev.business && <p className="mt-0.5 text-sm text-gb-silver">{rev.business}</p>}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setApproved(rev, !rev.approved)}
                data-testid={`review-approve-${rev.id}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                  rev.approved ? "border border-gb-silver/40 bg-gb-silver/10 text-gb-silverhi" : "btn-chrome"
                }`}
              >
                {rev.approved ? <X className="h-3.5 w-3.5" aria-hidden="true" /> : <Check className="h-3.5 w-3.5" aria-hidden="true" />}
                {rev.approved ? "Ocultar" : "Aprovar"}
              </button>
              <button type="button" onClick={() => remove(rev)} data-testid={`review-delete-${rev.id}`} aria-label={`Remover avaliação de ${rev.name}`} className="rounded-lg border border-white/10 p-2 text-gb-gray transition-colors hover:border-red-500/40 hover:text-red-400">
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-gb-gray">“{rev.comment}”</p>
          <p className="mt-3 font-mono text-[10px] text-gb-gray/60">
            {new Date(rev.created_at).toLocaleString("pt-BR")} · {rev.approved ? "visível no site" : "aguardando aprovação"}
          </p>
        </li>
      ))}
    </ul>
  );
};

export const AdminDashboard = ({ user, onLogout }) => {
  const [tab, setTab] = useState("reviews");

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    onLogout();
  };

  return (
    <div className="min-h-screen bg-gb-black" data-testid="admin-dashboard">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/assets/logo.jpg" alt="Logotipo GOBAX" width={36} height={36} className="h-9 w-9 rounded-full ring-1 ring-white/20" />
            <div className="leading-none">
              <p className="font-display text-sm font-bold">Painel GOBAX</p>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-gb-gray">{user.email}</p>
            </div>
          </div>
          <button type="button" onClick={logout} data-testid="admin-logout-btn" className="btn-ghost-chrome inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-gb-white">
            <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
            Sair
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div className="mb-8 flex flex-wrap gap-2" role="tablist" aria-label="Seções do painel">
          {[
            { id: "reviews", label: "Avaliações", icon: Star },
            { id: "projects", label: "Projetos", icon: FolderKanban },
            { id: "leads", label: "Leads", icon: Users },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold transition-colors ${
                tab === t.id ? "bg-gb-silver text-gb-black" : "border border-white/10 text-gb-gray hover:text-gb-white"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" aria-hidden="true" />
              {t.label}
            </button>
          ))}
        </div>
        {tab === "reviews" && <ReviewsTab />}
        {tab === "projects" && <ProjectsTab />}
        {tab === "leads" && <LeadsTab />}
      </main>
    </div>
  );
};
